import { Permissions } from "@repo/database";
import { EmployeeGroupSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { EmployeeGroup } from "~/providers/employee-group-provider";
import { useEmployeeGroupManager } from "~/managers/use-employee-group-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { MultiSelect } from "../ui/multi-select";

export default function EmployeeGroupForm({
  employeeGroup,
  onClose,
}: {
  employeeGroup?: EmployeeGroup;
  onClose: (force?: boolean) => void;
}) {
  const { t, i18n } = useTranslation("common");

  const { setUnsavedChanges } = useUnsavedChanges();

  const { createEmployeeGroup, updateEmployeeGroup } =
    useEmployeeGroupManager();

  const form = useForm({
    schema: EmployeeGroupSchema(i18n),
    defaultValues: {
      name: employeeGroup?.name ?? "",
      permissions: employeeGroup?.permissions ?? [],
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (employeeGroup) {
      const { data, error } = await updateEmployeeGroup({
        id: employeeGroup.id,
        ...formData,
      });
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    } else {
      const { data, error } = await createEmployeeGroup(formData);
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });
  return (
    <Form {...form}>
      <form
        id="employee-group-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("EmployeeGroup.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("EmployeeGroup.name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="permissions"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("EmployeeGroup.permissions")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select permissions..."
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  options={Object.values(Permissions).map((permission) => ({
                    value: permission,
                    label: permission,
                  }))}
                  defaultValue={field.value}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <FormMessage className="col-span-full">
            {form.formState.errors.root.message}
          </FormMessage>
        )}
      </form>
    </Form>
  );
}
